<template>
  <section class="page-stack">
    <article class="card page-hero-card">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Control Room</p>
          <h2>Application settings</h2>
          <p>Appearance, app updates, installation state and local saved data controls.</p>
        </div>
      </header>
    </article>

    <div class="settings-grid">
      <article class="card">
        <header>
          <div><p class="eyebrow">Appearance</p><h3>Theme</h3></div>
        </header>
        <section class="settings-row">
          <div>
            <strong>Claude theme</strong>
            <p>Basecoat Claude is active. Current mode: {{ themeModeLabel }}.</p>
          </div>
          <div class="page-actions">
            <button class="btn-icon-outline" type="button" aria-label="Toggle theme" @click="toggleTheme">
              <i v-if="themeMode === 'dark'" class="fa-solid fa-sun"></i>
              <i v-else class="fa-solid fa-moon"></i>
            </button>
          </div>
        </section>
      </article>

      <article class="card">
        <header>
          <div><p class="eyebrow">Updates</p><h3>Update application</h3></div>
        </header>
        <section class="settings-row">
          <div>
            <strong>{{ updateAvailable ? 'New version is ready' : 'Refresh to the latest build' }}</strong>
            <p>{{ updateAvailable
              ? 'Apply the latest version now. Favorites, compare list, and local settings will stay untouched.'
              : 'Pull the latest app files and refresh the dashboard in place without clearing local data.' }}</p>
          </div>
          <div class="page-actions">
            <button class="btn" type="button" aria-label="Update application now" title="Update application now" @click="refreshApplication">
              <i class="fa-solid fa-rotate"></i>
              Update
            </button>
          </div>
        </section>
      </article>

      <article class="card">
        <header>
          <div><p class="eyebrow">Install</p><h3>Install application</h3></div>
        </header>
        <section class="settings-row">
          <div>
            <strong>{{ installStatusTitle }}</strong>
            <p>{{ installStatusDescription }}</p>
          </div>
          <div class="page-actions">
            <button
              class="btn-icon"
              type="button"
              :disabled="!canTriggerInstall"
              :aria-label="installActionLabel"
              :title="installActionLabel"
              @click="handleInstallAction"
            >
              <i :class="installActionIcon"></i>
            </button>
          </div>
        </section>
      </article>

      <article class="card">
        <header>
          <div><p class="eyebrow">Data</p><h3>Local data</h3></div>
        </header>
        <section class="settings-row">
          <div>
            <strong>Favorites and compare list</strong>
            <p>Clear local app data without changing anything.</p>
          </div>
          <button class="btn-destructive" type="button" @click="clearLocalData">
            <i class="fa-solid fa-trash"></i>
          </button>
        </section>
      </article>

      
      <article class="card settings-background-card">
        <header>
          <div><p class="eyebrow">Appearance</p><h3>Background</h3></div>
        </header>
        <section class="settings-background-panel">
          <div class="settings-row settings-row-text">
            <div>
              <strong>Ambient application backdrop</strong>
              <p>Choose a subdued background image that quietly sits behind the interface.</p>
            </div>
          </div>

          <div class="background-picker-grid">
            <button
              v-for="background in backgroundOptions"
              :key="background.id"
              class="background-option-card"
              :class="{ 'is-active': background.id === appBackground }"
              type="button"
              :aria-pressed="background.id === appBackground"
              :title="background.label"
              @click="setBackground(background.id)"
            >
              <span
                class="background-option-thumb"
                :class="{ 'is-empty': !background.thumbnail }"
                :style="background.thumbnail ? { backgroundImage: `url(${background.thumbnail})` } : {}"
              >
                <i v-if="!background.thumbnail" class="fa-solid fa-ban"></i>
              </span>
              <span class="background-option-meta">
                <strong>{{ background.label }}</strong>
                <small>{{ background.id === appBackground ? 'Active' : 'Apply' }}</small>
              </span>
            </button>
          </div>
        </section>
      </article>
    </div>

    <div
      v-if="isInstallHelpOpen"
      class="app-modal-backdrop"
      :class="{ 'is-closing': closingInstallHelp }"
      role="presentation"
      @click.self="closeInstallInstructions"
    >
      <article class="app-modal settings-install-modal" role="dialog" aria-modal="true" aria-labelledby="ios-install-title">
        <header class="modal-head">
          <div>
            <p class="eyebrow">Install application</p>
            <h2 id="ios-install-title">Install on iPhone or iPad</h2>
            <p>Add the dashboard to your Home Screen from Safari to use it like an app.</p>
          </div>
          <button class="btn-icon-outline" type="button" aria-label="Close" title="Close" @click="closeInstallInstructions">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <section class="install-instructions">
          <ol>
            <li>Open this dashboard in <strong>Safari</strong>.</li>
            <li>Tap the <strong>Share</strong> button in the browser toolbar.</li>
            <li>Select <strong>Add to Home Screen</strong>.</li>
            <li>Confirm the name and tap <strong>Add</strong>.</li>
          </ol>

          <p class="install-tip">
            If you opened the app inside Chrome, Telegram, Discord, or another in-app browser, first switch to Safari.
          </p>

          <div class="modal-actions">
            <button class="btn" type="button" @click="closeInstallInstructions">Got it</button>
          </div>
        </section>
      </article>
    </div>
  </section>
</template>

<script>
import { BACKGROUND_OPTIONS } from '../constants/backgrounds'

export default {
  name: 'SettingsView',
  data() {
    return {
      isInstallHelpOpen: false,
      closingInstallHelp: false,
    }
  },
  computed: {
    theme() {
      return this.$store.getters['getTheme']
    },
    themeMode() {
      return this.$store.getters['getThemeMode']
    },
    appBackground() {
      return this.$store.getters['getAppBackground']
    },
    backgroundOptions() {
      return BACKGROUND_OPTIONS
    },
    themeModeLabel() {
      return this.themeMode === 'dark' ? 'dark' : 'light'
    },
    canInstall() {
      return this.$store.getters['getCanInstall']
    },
    updateAvailable() {
      return this.$store.getters['getUpdateAvailable']
    },
    isStandalone() {
      if (typeof window === 'undefined') return false

      return Boolean(
        window.matchMedia?.('(display-mode: standalone)').matches
        || window.navigator.standalone === true,
      )
    },
    isIosDevice() {
      if (typeof navigator === 'undefined') return false

      const userAgent = navigator.userAgent || ''
      const platform = navigator.platform || ''
      return /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    },
    canTriggerInstall() {
      return this.canInstall || this.isIosDevice
    },
    installStatusTitle() {
      if (this.isStandalone) return 'Already installed on this device'
      if (this.canInstall) return 'Ready to install'
      if (this.isIosDevice) return 'Install from Safari'
      return 'Install is unavailable now'
    },
    installStatusDescription() {
      if (this.isStandalone) {
        return 'The dashboard is already installed and ready to launch from your device like a standalone app.'
      }

      if (this.canInstall) {
        return 'Install the dashboard on desktop or mobile for fast access without reopening the browser tab.'
      }

      if (this.isIosDevice) {
        return 'On iPhone and iPad, install works through Safari using Add to Home Screen. Open the steps to continue.'
      }

      return 'Open the dashboard in a supported browser state to install it on desktop or mobile.'
    },
    installActionLabel() {
      if (this.isStandalone) return 'Application is already installed'
      if (this.isIosDevice) return 'Show iPhone and iPad install steps'
      return 'Install application'
    },
    installActionIcon() {
      if (this.isStandalone) return 'fa-solid fa-circle-check'
      if (this.isIosDevice) return 'fa-solid fa-download'
      return 'fa-solid fa-download'
    },
  },
  methods: {
    toggleTheme() {
      this.$store.dispatch('toggleTheme')
    },
    setBackground(backgroundId) {
      this.$store.dispatch('setAppBackground', backgroundId)
    },
    promptInstall() {
      this.$store.dispatch('promptInstall')
    },
    handleInstallAction() {
      if (this.isStandalone) return
      if (this.isIosDevice) {
        this.openInstallInstructions()
        return
      }

      this.promptInstall()
    },
    openInstallInstructions() {
      this.closingInstallHelp = false
      this.isInstallHelpOpen = true
      document.body.classList.add('is-scroll-locked')
    },
    closeInstallInstructions() {
      if (!this.isInstallHelpOpen || this.closingInstallHelp) return
      this.closingInstallHelp = true
      window.setTimeout(() => {
        this.isInstallHelpOpen = false
        this.closingInstallHelp = false
        document.body.classList.remove('is-scroll-locked')
      }, 180)
    },
    checkForUpdates() {
      this.$store.dispatch('checkForUpdates')
    },
    refreshApplication() {
      this.$store.dispatch('refreshApplication')
    },
    applyUpdate() {
      this.$store.dispatch('applyUpdate')
    },
    clearLocalData() {
      this.$store.dispatch('requestConfirmation', {
        title: 'Clear local data?',
        message: 'Remove all favorites and compare data saved on this device?',
        confirmLabel: 'Clear data',
        action: 'clearLocalData',
      })
    },
    onKeydown(event) {
      if (event.key === 'Escape' && this.isInstallHelpOpen) {
        this.closeInstallInstructions()
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
    document.body.classList.remove('is-scroll-locked')
  },
}
</script>
